import { useCallback, useEffect, useRef, useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthSessionProvider } from '../features/auth/session'
import { NotificationToastListener } from '../features/notifications/components/NotificationToastListener'
import { RealtimeEvents } from '../features/realtime/RealtimeEvents'
import { queryClient } from '../lib/query'
import { router } from './router'
import { ThemeProvider, useTheme } from './ThemeProvider'

function ToasterWithTheme() {
  const { resolvedTheme } = useTheme()
  const [expand, setExpand] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const toasterRef = useRef<HTMLElement>(null)

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = undefined
    }
    setExpand(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setExpand(false)
    }, 400)
  }, [])

  useEffect(() => {
    const el = toasterRef.current
    if (!el) return

    el.addEventListener('mouseenter', handleMouseEnter)
    el.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      el.removeEventListener('mouseenter', handleMouseEnter)
      el.removeEventListener('mouseleave', handleMouseLeave)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [handleMouseEnter, handleMouseLeave])

  return (
    <Toaster
      ref={toasterRef}
      position="bottom-right"
      richColors
      theme={resolvedTheme}
      expand={expand}
      visibleToasts={8}
    />
  )
}

export function AppProviders() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthSessionProvider>
          <RealtimeEvents />
          <RouterProvider router={router} />
          <ToasterWithTheme />
          <NotificationToastListener />
        </AuthSessionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
